import { removeAcquiredWishes } from './wishList.js'
const PERSISTED_KEYS = ['catalog_id', 'name', 'house', 'concentration', 'bottle_ml']

function text(value) {
  return String(value || '').trim()
}

export function ownershipIdentityKey(item) {
  return [item?.house, item?.name, item?.concentration]
    .map(value => text(value).toLocaleLowerCase())
    .join('\u0000')
}

// Collection acquisition deliberately persists identity + bottle size only.
// Catalog intelligence remains linked by catalog_id and is read later through
// collection_display; it is never copied into the ownership write.
export function toOwnershipPayload(item, bottleMl = item?.bottle_ml) {
  const catalogLinked = Boolean(item?.catalog_id || item?.id)
  const payload = {
    catalog_id: item?.catalog_id || item?.id || null,
    name: text(item?.name),
    house: text(item?.house),
    concentration: text(item?.concentration) || null,
    bottle_ml: bottleMl === '' || bottleMl == null ? null : Number(bottleMl),
  }

  // Catalog-linked adds keep concentration required. Catalog misses may omit it.
  if (!payload.name || !payload.house || (catalogLinked && !payload.concentration)) {
    throw new Error(catalogLinked
      ? 'House, fragrance name, and concentration are required.'
      : 'House and fragrance name are required.')
  }
  // One decimal place so 1.5 ml samples save (FOS-SAMPLE-TRAVEL-001; the bottle row keeps
  // the exact size, the collection row rounds it).
  if (payload.bottle_ml != null && (!Number.isFinite(payload.bottle_ml) || payload.bottle_ml <= 0 || payload.bottle_ml > 1000
    || Math.round(payload.bottle_ml * 10) !== payload.bottle_ml * 10)) {
    throw new Error('Bottle size must be more than 0 ml and at most 1000 ml, like 1.5 or 100.')
  }
  return payload
}

export function sanitizeOwnershipBatch(items) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Choose at least one fragrance to add.')
  }
  if (items.length > 100) throw new Error('Add up to 100 bottles at a time.')

  const seen = new Set()
  return items.map(item => {
    const payload = toOwnershipPayload(item)
    const key = ownershipIdentityKey(payload)
    if (seen.has(key)) throw new Error('Each selected fragrance variant must be distinct.')
    seen.add(key)
    return payload
  })
}

export async function persistOwnershipBatch(client, items) {
  const payload = sanitizeOwnershipBatch(items)
  const { data, error } = await client.rpc('add_collection_items_atomic_v1', {
    p_items: payload,
  })
  if (error) throw error
  if (!Array.isArray(data) || data.length !== payload.length) {
    throw new Error('The collection sync returned an incomplete batch.')
  }
  await removeAcquiredWishes(client, data)
  enqueueManualAddCandidates(client, data)
  return data
}

// Candidate research is intentionally downstream of the shelf write. The
// caller never awaits these requests: an intelligence outage cannot undo or
// delay a bottle that the atomic acquisition RPC has already persisted.
export function enqueueManualAddCandidates(client, rows) {
  const manualRows = (rows || []).filter(row => row?.record_origin === 'manual' && row?.id)
  for (const row of manualRows) {
    let request
    try {
      request = client.rpc('fn_enqueue_manual_add_candidate', {
        p_fragrance_id: row.id,
      })
    } catch {
      continue
    }
    void Promise.resolve(request).catch(() => undefined)
  }
}

export function optimisticCollectionRows(items, userId, createId = () => crypto.randomUUID()) {
  const now = new Date().toISOString()
  return sanitizeOwnershipBatch(items).map((payload, index) => ({
    ...payload,
    id: createId(index),
    user_id: userId,
    record_origin: payload.catalog_id ? 'catalog' : 'manual',
    bottle_count: 1,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _optimistic: true,
    _client_image: items[index]?.card_image || null,
    card_image: items[index]?.card_image || null,
  }))
}

export const OWNERSHIP_PERSISTED_KEYS = Object.freeze([...PERSISTED_KEYS])
